import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_ITEM_BY_ID_QUERY, GET_ITEMS_QUERY } from '../../queries/ItemQueries';
import { Item, UnitType, unitTypeToUnit, unitTypeToUnits } from '../../models/Item';
import { DELETE_ITEM_BY_ID_MUTATION } from '../../mutations/ItemMutations';
import { FormControlLabel, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { InventoryItem } from '../../models/InventoryItem';
import dayjs from "dayjs";
import { toUSCurrency } from '../../utilities/CurrencyUtilities';
import { ProductItem } from '../../models/ProductItem';

export default function ItemShow() {
  const { itemId } = useParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getItemById, { refetch: refetchItem }] = useLazyQuery(
    GET_ITEM_BY_ID_QUERY);

  const [deleteItemById] = useMutation(
    DELETE_ITEM_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_ITEMS_QUERY }],
  });

  const [item, setItem] = useState<Item | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [includeArchivedInventoryItems, setIncludeArchivedInventoryItems] = useState(false);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(itemId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getIngredientByIdError } = await getItemById(
          {
            variables: {
              id,
              inventoryItemsFilter: includeArchivedInventoryItems ? {} : {
                isArchived: { eq: false }
              }
            }
          });

        setIsLoading(false);
        setError(null);

        if (getIngredientByIdError) {
          setError(getIngredientByIdError);
        }

        if (data?.item) {
          setItem(data.item);
        } else {
          setError({ message: 'No item found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getItemById, includeArchivedInventoryItems, itemId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = useCallback(() => {
    navigate(`/dashboard/items/${itemId}/edit`);
  }, [itemId, navigate]);

  const handleDelete = useCallback(async () => {
    if (!item) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete ${item.name}?`,
      {
        title: `Delete item?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsLoading(true);
      try {
        const { data: result } = await deleteItemById({ variables: { id: item.id } });

        if (result?.deleted) {
          navigate('/dashboard/items');

          notifications.show('Item deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete item. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete item. Reason:' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsLoading(false);
    }
  }, [item, dialogs, deleteItemById, navigate, notifications]);

  const handleCheckboxFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setIncludeArchivedInventoryItems(event.target.checked);

      const id = +(itemId || '0');

      refetchItem(
        {
          id,
          inventoryItemsFilter: event.target.checked ? {} : {
            isArchived: { eq: false }
          }
        });
    },
    [itemId, refetchItem],
  );

  const handleBack = useCallback(() => {
    navigate('/dashboard/items');
  }, [navigate]);

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return item ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Name</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {item.name}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Description</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {item.description || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Unit</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {item.unitType}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Average Cost per Unit</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                ${(item.averageCostPerUnit ?? 0).toFixed(2)} /{unitTypeToUnit(item.unitType)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Standard Shipping Qty</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {(item.standardShippingQuantity || 0).toFixed(2)} {unitTypeToUnits(item.unitType)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Current QOH</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {((item.inventoryItems || []).map(item => (item.quantityOnHand ?? 0)).reduce((a, b) => a + b, 0) || 0).toFixed(3)} {unitTypeToUnits(item.unitType)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Min Order Qty</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {(item.minOrderQuantity || 0).toFixed(2)} {unitTypeToUnits(item.unitType)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Max Order Qty</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {(item.maxOrderQuantity || 0).toFixed(2)} {unitTypeToUnits(item.unitType)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Order Notes</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {item.orderNotes || 'N/A'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ width: '100%', mt: 2 }}>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Stack
                direction="row"
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  Inventory Items
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeArchivedInventoryItems}
                        onChange={handleCheckboxFieldChange}
                        size="small"
                        slotProps={{
                          input: { 'aria-label': 'controlled' },
                        }} />}
                    label="Show Archived Inventory Items" />
                </Box>
              </Stack>
              {(item.inventoryItems || []).length === 0 ?
                <span style={{ color: '#fac1c0' }}>No Items (Out of Stock)</span> :
                <TableContainer sx={{ maxHeight: '125px' }}>
                  <Table stickyHeader sx={{ minWidth: 500 }} aria-label="dense table" size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} component="th" scope="row" align="left">Lot #</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="left">Vendor</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Ordered Qty ({unitTypeToUnits(item?.unitType)})</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Ordered Date</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Total Cost per Unit ($/{unitTypeToUnit(item?.unitType)})</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} component="th" scope="row" align="right">Total Ordered Cost ($)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(item.inventoryItems || []).map((inventoryItem: InventoryItem) => (
                        <TableRow
                          key={inventoryItem.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: inventoryItem.isArchived ? '#fac1c0' : '' }}
                        >
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} align="left">
                            <a href={`/dashboard/inventory-items/${inventoryItem.id}`}>{inventoryItem.lotNumber || 'N/A'}</a>
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="left">
                            <a href={`/dashboard/vendors/${inventoryItem.vendorId}`}>{inventoryItem.vendor?.name}</a>
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                            {inventoryItem.quantityOrdered.toFixed(3)} {unitTypeToUnits(inventoryItem.item?.unitType as UnitType)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                            <a href={`/dashboard/purchase-orders/${inventoryItem.purchaseOrderId}`}>{dayjs(inventoryItem.purchaseOrder?.dateTimePurchased).format('M/D/YYYY')}</a>
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                            {toUSCurrency(inventoryItem.totalCostPerUnit)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '15%' }} align="right">
                            {toUSCurrency(inventoryItem.totalOrderedCost)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
            </Paper>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ width: '100%', mt: 2 }}>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Products</Typography>
              {(item.productItems || []).length === 0 ?
                <Box>None</Box> :
                <TableContainer sx={{ maxHeight: '125px' }}>
                  <Table stickyHeader sx={{ minWidth: 500 }} aria-label="dense table" size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} component="th" scope="row" align="left">Name</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} component="th" scope="row" align="right">Quantity (lbs)</TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} component="th" scope="row" align="right">Qty Needed for 50# (lbs)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(item.productItems || []).map((productItem: ProductItem) => (
                        <TableRow
                          key={productItem.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '50%' }} align="left">
                            <a href={`/dashboard/products/${productItem.productId}`}>{productItem.product?.name}</a>
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} align="right">
                            {productItem.quantity.toFixed(3)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '25%' }} align="right">
                            {productItem.standardProductBatchQuantity?.toFixed(3)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
            </Paper>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Stack>
        </Stack>
      </Box>
    ) : null;
  }, [isLoading, error, item, includeArchivedInventoryItems, handleCheckboxFieldChange, handleBack, handleEdit, handleDelete]);

  const itemName = item?.name || 'Item';

  const pageTitle = `${itemName} (${item?.isIngredient ? 'Ingredient' : 'Item'})`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Items', path: '/dashboard/items' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
